import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const TripCategory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ id: '', name: '', isFeatured: false });
  const [image, setImage] = useState(null);
  const [icon, setIcon] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [iconPreview, setIconPreview] = useState('');
  const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || '';

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('tripCategoryList');
      setCategories(response.data || []);
    } catch (error) {
      toast.error('Failed to load categories');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData({ id: '', name: '', isFeatured: false });
    setImage(null);
    setIcon(null);
    setImagePreview('');
    setIconPreview('');
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setFormData({ id: category._id, name: category.name, isFeatured: category.isFeatured || false });
    setImage(null);
    setIcon(null);
    setImagePreview(category.image ? `${MEDIA_URL}${category.image}` : '');
    setIconPreview(category.icon ? `${MEDIA_URL}${category.icon}` : '');
    setShowModal(true);
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1 * 1024 * 1024) {
        toast.error('Please choose a smaller image less than 1MB');
        e.target.value = '';
        return;
      }
      if (type === 'image') {
        setImage(file);
        setImagePreview(URL.createObjectURL(file));
      } else {
        setIcon(file);
        setIconPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const data = new FormData();
      if (formData.id) data.append('id', formData.id);
      data.append('name', formData.name);
      data.append('isFeatured', formData.isFeatured ? 'true' : 'false');
      if (image) data.append('image', image);
      if (icon) data.append('icon', icon);

      const response = await apiService.postFormData('updateTripCategory', data);
      if (!response.success) {
        toast.success('Category updated successfully');
        setShowModal(false);
        fetchCategories();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusToggle = async (categoryId) => {
    try {
      await apiService.get(`tripCategoryStatus/${categoryId}`);
      setCategories(categories.map(c => c._id === categoryId ? { ...c, status: c.status === 1 ? 0 : 1 } : c));
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error('Status error:', error);
    }
  };

  const handleFeaturedToggle = async (categoryId) => {
    try {
      await apiService.get(`tripCategoryFeaturedStatus/${categoryId}`);
      setCategories(categories.map(c => c._id === categoryId ? { ...c, isFeatured: !c.isFeatured } : c));
      toast.success('Featured status updated');
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error('Featured status error:', error);
    }
  };

  const canEdit = (category) => {
    return user?.role === 'admin' || category.vendorId === user?._id;
  };

  return (
    <div className="p-0">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-3 sm:p-4 border-b flex justify-end">
            <button
              onClick={handleAddNew}
              className="px-2 sm:px-4 py-2 text-white rounded-lg flex items-center gap-2"
              style={{ backgroundColor: '#009ef7' }}
            >
              <i className="bi bi-plus-lg"></i>
              Add new
            </button>
          </div>

          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12">
                <i className="bi bi-signpost text-4xl text-gray-300 mb-2"></i>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No Categories Found</h3>
                <p className="text-gray-500">Add your first category to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-500 text-xs uppercase">
                      <th className="px-2 sm:px-4 py-3">Name</th>
                      <th className="px-2 sm:px-4 py-3">Image</th>
                      <th className="px-2 sm:px-4 py-3">Icon</th>
                      <th className="px-2 sm:px-4 py-3">Status</th>
                      <th className="px-2 sm:px-4 py-3">Featured (Home)</th>
                      <th className="px-2 sm:px-4 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {categories.map((category) => (
                      <tr key={category._id} className="hover:bg-gray-50">
                        <td className="px-2 sm:px-4 py-3 text-sm">{category.name}</td>
                        <td className="px-2 sm:px-4 py-3">
                          <img
                            src={category.image ? `${MEDIA_URL}${category.image}` : '/assets/media/avatars/300-1.jpg'}
                            alt={category.name}
                            className="h-16 w-24 sm:h-24 sm:w-36 object-cover rounded-lg"
                          />
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          <img
                            src={category.icon ? `${MEDIA_URL}${category.icon}` : '/assets/media/avatars/300-1.jpg'}
                            alt={category.name}
                            className="h-12 w-12 sm:h-20 sm:w-20 object-cover rounded-lg"
                          />
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          {canEdit(category) && (
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={category.status === 1}
                                onChange={() => handleStatusToggle(category._id)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" style={category.status === 1 ? { backgroundColor: '#009ef7' } : {}}></div>
                            </label>
                          )}
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          {canEdit(category) && (
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={category.isFeatured === true}
                                onChange={() => handleFeaturedToggle(category._id)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" style={category.isFeatured ? { backgroundColor: '#f59e0b' } : {}}></div>
                            </label>
                          )}
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-center">
                          {canEdit(category) && (
                            <button
                              onClick={() => handleEdit(category)}
                              className="text-green-600 hover:text-green-800"
                            >
                              <i className="bi bi-pencil text-lg"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Update Category</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Category Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Category name"
                  required
                />
              </div>

              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isFeatured"
                  checked={formData.isFeatured || false}
                  onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="isFeatured" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                  Show on Home Page (Choose Your Vibe section)
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Category Image <span className="text-red-500">*</span></label>
                  <div className="relative w-full h-36 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                    {imagePreview && <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />}
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      onChange={(e) => handleImageChange(e, 'image')}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      required={!formData.id}
                    />
                    {!imagePreview && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-gray-400">Click to upload</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category Icon <span className="text-red-500">*</span></label>
                  <div className="relative w-full h-36 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                    {iconPreview && <img src={iconPreview} alt="Preview" className="w-full h-full object-contain" />}
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      onChange={(e) => handleImageChange(e, 'icon')}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      required={!formData.id}
                    />
                    {!iconPreview && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-gray-400">Click to upload</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  className="px-2 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-2 sm:px-4 py-2 text-white rounded-lg disabled:opacity-50"
                  style={{ backgroundColor: '#009ef7' }}
                >
                  {submitting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></span>
                      Please wait...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripCategory;
